import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { FieldErrors, FieldValues, Path, UseFormRegister } from 'react-hook-form';

interface Props<T extends FieldValues> {
  className?: string;
  errors: FieldErrors<T>;
  label: string;
  maxLength?: number;
  name: Path<T>;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
  register: UseFormRegister<T>;
  required?: boolean;
  rows?: number;
}

export const TextAreaField = <T extends FieldValues>({
  className,
  errors,
  label,
  maxLength,
  name,
  onChange,
  placeholder,
  register,
  required,
  rows = 3,
}: Props<T>) => {
  const { t } = useTranslationWithPrefix('components.textArea');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
    if (onChange) onChange(e);
  };

  return (
    <Form.Group className={`mb-3 w-full max-w-full ${className || ''}`}>
      <label className="small pb-1">
        {label}
        {required ? '*' : ''}
      </label>
      <Form.Control
        as="textarea"
        required={required}
        rows={rows}
        {...register(name, {
          maxLength: maxLength,
          onChange: handleChange,
        })}
        isInvalid={!!errors[name]}
        maxLength={maxLength}
        placeholder={placeholder}
      />
      {maxLength && (
        <div className="text-muted small mt-1">
          {t('characterCount', { current: charCount, max: maxLength })}
        </div>
      )}
      <Form.Control.Feedback type="invalid">
        {errors[name]?.message as string}
      </Form.Control.Feedback>
    </Form.Group>
  );
};
